import { Component, CUSTOM_ELEMENTS_SCHEMA, Input, OnInit, AfterViewInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Router } from '@angular/router';
import { FormBuilder, FormGroup } from '@angular/forms';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { ReplaySubject, takeUntil } from 'rxjs';
import { AuthPolicies } from '../../../auth/auth-policies';
import { RepairLocationService } from '../../../services/repair-location.service';
import { RepairLocationListItem } from '../../../models/repair-location-list-item.model';
import { RepairLocationFilter } from '../../../models/repair-location-filter.model';
import { RepairLocationFilterForm } from '../../../form-model/repair-location-filter.form.model';
import { RepairLocationSearchTextForm } from '../../../form-model/repair-location-search-text.form.model';
import { RepairLocationGridColumns } from './repair-location-grid.config';
import { LoaderComponent } from '../../../shared-components/loader/loader.component';
import { APICallStatus } from '../../../models/api-call-status.mode';
import '@maersk-global/mds-components-core/mc-table';
import '@maersk-global/mds-components-core/mc-pagination';
import '@maersk-global/mds-components-core/mc-select';
import '@maersk-global/mds-components-core/mc-option';
import '@maersk-global/mds-components-core/mc-button';
import '@maersk-global/mds-components-core/mc-icon';

@Component({
  selector: 'app-repair-location-grid',
  imports: [CommonModule, RouterModule, NgxSkeletonLoaderModule, LoaderComponent],
  templateUrl: './repair-location-grid.component.html',
  styleUrl: './repair-location-grid.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class RepairLocationGridComponent implements OnInit, AfterViewInit, OnDestroy {
  data: RepairLocationListItem[] = [];
  mockDataForLoadingAnimation: { id: number }[] = Array.from({ length: 20 }, (_, i) => ({ id: i }));

  firstPage: number | undefined;
  previousPage: number | undefined;
  currentPage = 1;
  nextPage: number | undefined;
  lastPage: number | undefined;
  totalItemCount: number | undefined;
  pageLimit = 20;
  sort: string | undefined;
  filterParam: RepairLocationFilter = {};
  getRepairLocationApiCallStatus: APICallStatus = 'NOT_STARTED';

  columns = RepairLocationGridColumns;

  @Input({ required: true }) filterFormGroup!: FormGroup<{
    repairLocationFilterForm: FormGroup<RepairLocationFilterForm>;
    repairLocationSearchTextForm: FormGroup<RepairLocationSearchTextForm>;
  }>;

  private readonly destroyed$ = new ReplaySubject<boolean>(1);

  constructor(
    private readonly repairLocationService: RepairLocationService,
    private readonly router: Router,
    private readonly formBuilder: FormBuilder,
    public readonly authPolicy: AuthPolicies,
  ) {}

  ngOnInit(): void {
    this.getRepairLocationList();
  }

  ngAfterViewInit(): void {
    this.subscribeToFormChanges();
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  private subscribeToFormChanges(): void {
    this.filterFormGroup.controls.repairLocationFilterForm
      .get('applyRepairLocationFilter')!
      .valueChanges.pipe(takeUntil(this.destroyed$))
      .subscribe((applyVal) => {
        if (applyVal === true) {
          this.onApplyFilter();
        }
      });
    this.filterFormGroup.controls.repairLocationSearchTextForm
      .get('appliedRepairLocationSearchTextFilter')!
      .valueChanges.pipe(takeUntil(this.destroyed$))
      .subscribe((searchApplied) => {
        if (searchApplied === true) {
          this.onApplyFilter();
        }
      });
  }

  onSortDirectionChange(event: Event): void {
    const customEvent = event as CustomEvent;
    const sortColumn: string = customEvent.detail.column;
    const sortDirection: string = customEvent.detail.direction === 'ascending' ? 'asc' : 'desc';
    if (sortColumn !== 'actions') {
      this.sort = `${sortColumn}:${sortDirection}`;
      this.currentPage = 1;
      this.getRepairLocationList();
    }
  }

  onPageLimitChange(event: Event): void {
    const customEvent = event as CustomEvent;
    this.currentPage = 1;
    this.pageLimit = customEvent.detail.value;
    this.getRepairLocationList();
  }

  onPageChange(event: Event): void {
    const customEvent = event as CustomEvent;
    const changedPage = customEvent.detail as number;
    if (changedPage !== this.currentPage) {
      this.currentPage = changedPage;
      this.getRepairLocationList(true);
    }
  }

  onApplyFilter(): void {
    if (this.filterFormGroup.controls.repairLocationFilterForm.valid) {
      this.filterParam = this.extractFilterParams();
      this.currentPage = 1;
      this.getRepairLocationList();
    }
  }

  onEditClicked(code: string): void {
    this.router.navigate(['/master-data/repair-location', code, 'edit']);
  }

  clearFilter(): void {
    this.filterFormGroup.reset();
    this.filterFormGroup.controls.repairLocationSearchTextForm.get('appliedRepairLocationSearchTextFilter')?.setValue(true);
    this.filterFormGroup.controls.repairLocationFilterForm.get('applyRepairLocationFilter')?.setValue(true);
  }

  private extractFilterParams(): RepairLocationFilter {
    const filterForm = this.filterFormGroup.controls.repairLocationFilterForm;
    const filters: RepairLocationFilter = {};
    const codeList = filterForm.get('repairLocationCodeList')?.value;
    if (codeList && Array.isArray(codeList) && codeList.length > 0) filters.repairLocationCodeList = codeList;
    const searchText = this.filterFormGroup.controls.repairLocationSearchTextForm.get('SearchText')?.value;
    if (searchText) filters.SearchText = searchText;
    return filters;
  }

  getRepairLocationList(isPageChange = false): void {
    this.getRepairLocationApiCallStatus = 'IN_PROGRESS';
    this.repairLocationService
      .getRepairLocationList(this.currentPage, this.pageLimit, this.sort, this.filterParam)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (response) => {
          const headers = response.headers;
          this.data = (response.body || []).map((item) => ({ ...item, id: item.code }));
          if (headers) {
            this.firstPage = headers.get('First-Page') ? +headers.get('First-Page')! : undefined;
            this.previousPage = headers.get('Previous-Page') ? +headers.get('Previous-Page')! : undefined;
            this.currentPage = +headers.get('Current-Page')! || this.currentPage;
            this.nextPage = headers.get('Next-Page') ? +headers.get('Next-Page')! : undefined;
            this.lastPage = headers.get('Last-Page') ? +headers.get('Last-Page')! : undefined;
            this.totalItemCount = headers.get('Total-Item-Count') ? +headers.get('Total-Item-Count')! : undefined;
          }
          this.getRepairLocationApiCallStatus = 'COMPLETED_SUCCESFULLY';
        },
        error: () => {
          this.getRepairLocationApiCallStatus = 'FAILED';
          this.data = [];
        },
      });
  }
}
